import { CdaCallout } from '../../components/CdaCallout';
import { COSMETIC_ACTIVES, COSMETIC_CATEGORIES } from '../../data/cosmeticActives';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, FlaskConical, TrendingUp, Leaf, FileCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';
import { SEO_CONTENT } from '../../data/seoContent';

export const PersonalCare = () => {
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener('copy', prevent);
    document.addEventListener('contextmenu', prevent);
    return () => {
      document.removeEventListener('copy', prevent);
      document.removeEventListener('contextmenu', prevent);
    };
  }, []);
  const offeringItems = [
    {
      icon: FlaskConical,
      title: "CRDMO/CDMO Services",
      description: "End-to-end support from eco-conscious research and development to scalable, low-waste manufacturing of active compounds such as antioxidants, Antifungal, UV filters, and naturally derived emollients."
    },
    {
      icon: TrendingUp,
      title: "Scalable Production",
      description: "Efficient scale-up from lab to commercial volume using processes designed to reduce carbon emissions, water usage, and waste generation."
    },
    {
      icon: Leaf,
      title: "Purification",
      description: "Sustainable purification techniques, including solvent recycling, low-energy crystallization to achieve high purity while minimizing environmental impact."
    },
    {
      icon: FileCheck,
      title: "Compliance & Transparency",
      description: "Full regulatory guidance and technical support for formulation and to help you meet sustainability goals and appeal to eco-aware consumers."
    }
  ];

  return (
    <div className="min-h-screen pt-20" style={{ userSelect: 'none' }}>
      <SEO {...SEO_CONTENT.industriesPersonalCare} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Heart className="w-12 h-12 md:w-16 md:h-16 text-teal-600 mb-4 md:mb-6" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">Personal Care Actives</h1>
          <p className="text-lg sm:text-xl md:text-2xl text-teal-400 font-medium">
            Sustainable, High-Performance Ingredients for Conscious Cosmetics
          </p>
        </div>
      </section>

      {/* Main Description Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                At LAMKO, we deliver high-value, sustainably produced active ingredients for cosmetic and personal care brands through integrated CRDMO/CDMO services that span custom molecule design, process development, scale-up, and reliable manufacturing. Our portfolio is centred on UV actives, antimicrobial systems for scalp and hair care, and well aging ingredients, giving formulators a focused toolbox for modern, performance driven skincare, suncare, and haircare products.
              </p>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                By embedding green chemistry principles, energy-efficient operations, and smart solvent and waste management into each project, we combine scientific excellence with measurable environmental responsibility. Our UV filters, scalp/hair antimicrobials, and well-ageing molecules are engineered for efficacy, purity, and formulation robustness, making them ideally suited for serums, creams, sunscreens, and well-ageing products where performance and a reduced ecological footprint are equally critical.
              </p>
            </div>
            <div>
              <img 
                src="/img/ext/9c946f8f2c.jpg" 
                alt="Personal Care Products" 
                className="rounded-lg shadow-2xl w-full h-auto object-cover" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sustainable Offerings Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8"><CdaCallout from="/industries/personal-care" /></div>
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Sustainable Offerings
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            {offeringItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-teal-600">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="text-teal-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-sm md:text-base text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-10 md:mt-12 text-center">
            <p className="text-base md:text-lg text-gray-700 font-medium max-w-3xl mx-auto">
              Leverage our CRDMO/CDMO capabilities to innovate with confidence in the competitive personal care industry.
            </p>
          </div>
        </div>
      </section>

        {/* Cosmetic actives product line */}
        <section className="py-12 md:py-20 bg-white" data-testid="pc-product-line">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <div className="text-xs font-bold tracking-widest text-orange-600 uppercase mb-2">Product line</div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">Cosmetic active ingredients</h2>
              <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                UV filters, scalp-care, brightening and preservation actives with INCI, CAS, IUPAC and typical specification on each page.
                Samples for evaluation, kilogram pilot batches and regular supply run under one project manager.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {COSMETIC_CATEGORIES.map((c) => (
                <div key={c.key} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-3">{c.label}</div>
                  <ul className="space-y-2">
                    {COSMETIC_ACTIVES.filter((a) => a.category === c.key).map((a) => (
                      <li key={a.slug}>
                        <Link to={`/products/cosmetic-actives/${a.slug}`} className="text-sm font-medium text-teal-800 hover:underline">{a.inci}</Link>
                        <div className="text-xs text-gray-500">CAS {a.cas}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                ['CRDMO', 'Feasibility, route scouting and lab-scale samples for a new or improved active. Impurity profiling and analytical method set-up included.'],
                ['CDMO', 'Process development, scale-up, pilot batches and technology transfer, then regular production with lot-by-lot COA.'],
                ['Documentation', 'TDS, SDS, COA, allergen and impurity statements; Halal and vegan documentation for specific actives; market-specific regulatory notes.'],
              ].map(([t, d]) => (
                <div key={t} className="rounded-xl border border-teal-200 bg-teal-50 p-5">
                  <div className="font-semibold text-gray-900">{t}</div>
                  <div className="text-sm text-gray-700 mt-1 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/products/cosmetic-actives"><Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white">View all actives<ArrowRight className="ml-2" size={20} /></Button></Link>
              <Link to="/partner/cosmetic-actives"><Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white">Start a cosmetic actives enquiry<ArrowRight className="ml-2" size={20} /></Button></Link>
            </div>
          </div>
        </section>


      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-teal-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6">Let's Innovate Your Personal Care Products</h2>
          <p className="text-base md:text-lg text-teal-100 mb-6 md:mb-8">
            One enquiry covers research (CRDMO), scale-up and supply (CDMO). A mutual CDA can be put in place first.
          </p>
          <Link to="/partner/cosmetic-actives">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white">
              Start a cosmetic actives enquiry
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
